import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ArrowRight } from "lucide-react";
import { PROJECTS, getProject } from "@/data/projects";

const Eyebrow = ({ children, num }) => (
  <div className="flex items-center gap-3">
    {num && (
      <span className="num-eyebrow text-[#0A0A0A]" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
        {num}
      </span>
    )}
    <span className="overline">{children}</span>
  </div>
);

// Animation configs hoisted to module scope to avoid recreating objects on every render
const FADE_IN_UP = { opacity: 0, y: 20 };
const FADE_IN_UP_LG = { opacity: 0, y: 24 };
const ANIMATE_IN_PLACE = { opacity: 1, y: 0 };
const VIEWPORT_ONCE = { once: true };
const trans = (i, base = 0.07) => ({ duration: 0.5, delay: i * base });

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = getProject(slug);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F7F7F7] px-6 text-center" data-testid="project-not-found">
        <Eyebrow num="404">Proyecto no encontrado</Eyebrow>
        <h1 className="font-display text-5xl uppercase mt-6">Este proyecto no existe.</h1>
        <Link to="/#proyectos" className="mt-10 inline-flex items-center gap-3 px-6 py-3 bg-[#0A0A0A] text-white text-[13px] uppercase tracking-wider">
          Volver a proyectos <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </Link>
      </div>
    );
  }

  const others = PROJECTS.filter((p) => p.slug !== project.slug).slice(0, 3);

  return (
    <div className="bg-[#F7F7F7] pt-[72px]" data-testid={`project-detail-${project.slug}`}>
      {/* Back bar */}
      <div className="mx-auto max-w-[1480px] px-6 lg:px-10 pt-10">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest hover:opacity-60 transition-opacity"
          data-testid="project-back-btn"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Volver
        </button>
      </div>

      {/* Hero */}
      <section className="mx-auto max-w-[1480px] px-6 lg:px-10 pt-10 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Eyebrow num={`P/${PROJECTS.findIndex((p) => p.slug === project.slug) + 1}`}>
              {project.sector}
            </Eyebrow>
            <h1 className="font-display uppercase mt-6 text-[clamp(2.5rem,7vw,6.5rem)] leading-[0.9]">
              {project.title}
            </h1>
            <p className="mt-6 text-[#555] text-lg">{project.subtitle}</p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-6">
              <div>
                <dt className="overline">Ubicación</dt>
                <dd className="font-heading text-xl mt-2">{project.place}</dd>
              </div>
              <div>
                <dt className="overline">Año</dt>
                <dd className="font-heading text-xl mt-2">{project.year}</dd>
              </div>
              <div className="col-span-2">
                <dt className="overline">Sector</dt>
                <dd className="font-heading text-xl mt-2">{project.sector}</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* Hero image */}
      <section className="mx-auto max-w-[1480px] px-6 lg:px-10">
        <div className="relative aspect-[16/10] overflow-hidden border border-black/10">
          <img src={project.hero} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />
        </div>
      </section>

      {/* Metrics */}
      <section className="mx-auto max-w-[1480px] px-6 lg:px-10 py-20 lg:py-28">
        <Eyebrow num="01">Datos del proyecto</Eyebrow>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 border border-black/10">
          {project.metrics.map((m, i) => (
            <motion.div
              key={`${m.k}-${m.v}`}
              initial={FADE_IN_UP}
              whileInView={ANIMATE_IN_PLACE}
              viewport={VIEWPORT_ONCE}
              transition={trans(i)}
              className="bg-[#F7F7F7] p-8 lg:p-10"
            >
              <div className="font-display text-4xl lg:text-5xl leading-none">{m.k}</div>
              <div className="overline mt-4">{m.v}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Summary */}
      <section className="mx-auto max-w-[1480px] px-6 lg:px-10 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Eyebrow num="02">Resumen</Eyebrow>
        </div>
        <p className="lg:col-span-8 text-[clamp(1.25rem,2.2vw,2rem)] font-light leading-snug text-[#0A0A0A]">
          {project.summary}
        </p>
      </section>

      {/* Challenge + Solution split */}
      <section className="mx-auto max-w-[1480px] px-6 lg:px-10 py-12 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
          <div className="bg-white p-10 lg:p-14">
            <Eyebrow num="03">El reto</Eyebrow>
            <p className="mt-8 text-[17px] leading-relaxed text-[#222]">{project.challenge}</p>
          </div>
          <div className="bg-[#0A0A0A] text-[#F7F7F7] p-10 lg:p-14">
            <div className="flex items-center gap-3">
              <span className="num-eyebrow text-white">04</span>
              <span className="overline text-white/60">La solución</span>
            </div>
            <p className="mt-8 text-[17px] leading-relaxed text-white/85">{project.solution}</p>
          </div>
        </div>
      </section>

      {/* Materials + Team */}
      <section className="mx-auto max-w-[1480px] px-6 lg:px-10 py-12 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6">
          <Eyebrow num="05">Materiales</Eyebrow>
          <ul className="mt-8 divide-y divide-black/15 border-t border-b border-black/15">
            {project.materials.map((m, i) => (
              <li key={m} className="py-4 flex items-baseline justify-between">
                <span className="font-heading text-xl">{m}</span>
                <span className="num-eyebrow text-[#888]">0{i + 1}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-5 lg:col-start-8">
          <Eyebrow num="06">Equipo</Eyebrow>
          <ul className="mt-8 space-y-5">
            {project.team.map((t) => (
              <li key={t.role} className="flex items-baseline justify-between gap-6 border-b border-dashed border-black/20 pb-3">
                <span className="overline">{t.role}</span>
                <span className="font-heading text-lg text-right">{t.name}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Quote */}
      {project.quote && (
        <section className="mx-auto max-w-[1480px] px-6 lg:px-10 py-16 lg:py-28">
          <div className="border-t border-b border-black/15 py-16 lg:py-24 text-center max-w-4xl mx-auto">
            <Eyebrow num="07">Testimonio</Eyebrow>
            <blockquote className="font-display text-[clamp(1.5rem,3.2vw,2.8rem)] mt-10 uppercase leading-[1.05]">
              «{project.quote.text}»
            </blockquote>
            <div className="mt-8 overline">{project.quote.author}</div>
          </div>
        </section>
      )}

      {/* Gallery */}
      {project.gallery?.length > 0 && (
        <section className="mx-auto max-w-[1480px] px-6 lg:px-10 pb-20">
          <Eyebrow num="08">Imágenes</Eyebrow>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {project.gallery.map((src, i) => (
              <motion.div
                key={src}
                initial={FADE_IN_UP_LG}
                whileInView={ANIMATE_IN_PLACE}
                viewport={VIEWPORT_ONCE}
                transition={trans(i, 0.1)}
                className="relative aspect-[4/3] overflow-hidden border border-black/10"
              >
                <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Next projects */}
      <section className="bg-[#0A0A0A] text-[#F7F7F7] py-20 lg:py-28">
        <div className="mx-auto max-w-[1480px] px-6 lg:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="flex items-center gap-3">
                <span className="num-eyebrow text-white">09</span>
                <span className="overline text-white/60">Otros proyectos</span>
              </div>
              <h2 className="font-display text-4xl lg:text-5xl uppercase mt-6">Sigue explorando.</h2>
            </div>
            <Link to="/#proyectos" className="inline-flex items-center gap-3 px-5 py-3 border border-white/40 hover:bg-white hover:text-black transition-colors text-[12px] uppercase tracking-widest">
              Ver todos <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/proyectos/${p.slug}`}
                className="group relative aspect-[4/5] overflow-hidden border border-white/15 block"
                data-testid={`related-project-${p.slug}`}
              >
                <img src={p.hero} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-transparent" />
                <div className="absolute left-6 bottom-6 right-6 flex items-end justify-between">
                  <div>
                    <div className="overline text-white/70">{p.place} · {p.year}</div>
                    <div className="font-heading text-xl mt-2">{p.title}</div>
                  </div>
                  <ArrowUpRight className="w-5 h-5" strokeWidth={1.5} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F7F7F7] py-20 lg:py-32">
        <div className="mx-auto max-w-[1480px] px-6 lg:px-10 text-center">
          <Eyebrow num="∞">Tu proyecto</Eyebrow>
          <h2 className="font-display text-5xl lg:text-7xl uppercase mt-8 leading-[0.95]">
            ¿Tu proyecto <br /> sigue?
          </h2>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/#contacto"
              className="inline-flex items-center gap-3 px-7 py-4 text-[13px] uppercase tracking-wider bg-[#0A0A0A] text-[#F7F7F7] hover:bg-[#333] transition-colors"
              data-testid="project-cta-presupuesto"
            >
              Solicitar presupuesto
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link
              to="/#configurador"
              className="inline-flex items-center gap-3 px-6 py-3 text-[13px] uppercase tracking-wider border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#F7F7F7] transition-colors"
              data-testid="project-cta-configurador"
            >
              Abrir configurador
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
